import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AgentLayout } from "@/components/AgentLayout";
import { SalesStatsPanel } from "@/components/SalesStatsPanel";
import { OnboardingPanel } from "@/components/OnboardingPanel";
import { AgentStatusBadge } from "@/components/AgentStatusBadge";
import { AgentEarningsCard } from "@/components/AgentEarningsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, CalendarBlank, MapPin, Trash, PencilSimple, CircleNotch, Suitcase, CheckCircle, Bell, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { formatDateShort } from "@/lib/dateUtils";

const formatDate = (iso) => formatDateShort(iso) || "—";

const STATUS_STYLES = {
  Draft: "bg-white/5 text-white/60 border-white/10",
  Sent: "bg-blue-500/10 text-blue-300 border-blue-400/30",
  Approved: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  Declined: "bg-amber-500/10 text-amber-300 border-amber-400/40",
  "Changes Requested": "bg-amber-500/10 text-amber-300 border-amber-400/40",
  Revised: "bg-blue-500/10 text-blue-300 border-blue-400/30",
  "Payment Link Sent": "bg-purple-500/10 text-purple-300 border-purple-400/40",
  "Payment Authorization Received": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  "Deposit Confirmed": "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  "Deposit Paid": "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  "Paid in Full": "bg-emerald-500/20 text-emerald-200 border-emerald-400/50",
  "Payment Received": "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  Booked: "bg-green-500/15 text-green-300 border-green-400/40",
};

const PAID_STATUSES = ["Deposit Paid", "Deposit Confirmed", "Paid in Full", "Payment Received"];

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.Draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${cls}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

export default function Dashboard() {
  const { user, refreshMe } = useAuth();
  const [itineraries, setItineraries] = useState(null);
  const [search, setSearch] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({
    title: "", client_name: "", client_email: "", client_phone: "",
    destination: "", start_date: "", end_date: "",
  });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/itineraries");
      setItineraries(data);
    } catch (e) {
      toast.error("Failed to load itineraries");
      setItineraries([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/itineraries", {
        title: draft.title,
        client_name: draft.client_name,
        client_email: draft.client_email,
        client_phone: draft.client_phone,
        destination: draft.destination,
        start_date: draft.start_date,
        end_date: draft.end_date,
      });
      setOpenNew(false);
      setDraft({ title: "", client_name: "", client_email: "", client_phone: "", destination: "", start_date: "", end_date: "" });
      navigate(`/itineraries/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        Array.isArray(detail)
          ? detail.map((e) => `${e.loc?.slice(-1)[0] || "field"}: ${e.msg}`).join(" · ")
          : detail || "Failed to create itinerary"
      );
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/itineraries/${id}`);
      toast.success("Itinerary deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleMarkBooked = async (id) => {
    try {
      await api.post(`/itineraries/${id}/mark-booked`);
      toast.success("Marked as booked — client notified");
      load();
    } catch {
      toast.error("Failed to mark as booked");
    }
  };

  // Split templates from agent's own trips so we can render them as a separate
  // "Templates" section above the regular grid. Templates also bypass the search box.
  const templates = (itineraries || []).filter((i) => i.is_template);
  const regularTrips = (itineraries || []).filter((i) => !i.is_template);
  const filtered = regularTrips.filter((i) => {
    const q = search.toLowerCase();
    return !q || i.title?.toLowerCase().includes(q) || i.client_name?.toLowerCase().includes(q) || i.destination?.toLowerCase().includes(q);
  });

  const handleDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/itineraries/${id}/duplicate`);
      toast.success("Template duplicated — start customizing!");
      navigate(`/itineraries/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to duplicate");
    }
  };

  return (
    <AgentLayout>
      {/* Onboarding panel — only renders if status pending OR not all 4 items done */}
      {user && user.role === "agent" && (
        <OnboardingPanel user={user} compact onRefresh={refreshMe} />
      )}

      {/* Earnings snapshot — agents only */}
      {user && user.role === "agent" && (
        <AgentEarningsCard user={user} />
      )}

      {/* Header */}
      <div className="mb-10 animate-fade-up">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Studio</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-white mb-3">
          Your <span className="italic text-[#D4AF37]">itineraries</span>
        </h1>
        <div className="flex items-center gap-3 mb-3">
          {user?.role === "agent" && user?.agent_status && (
            <AgentStatusBadge status={user.agent_status} size="md" data-testid="dashboard-status-badge" />
          )}
        </div>
        <p className="text-white/60 text-sm max-w-xl font-sans">
          Every journey, curated. Manage, edit, and share bespoke travel plans with your clients.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
          <Input
            placeholder="Search by client, destination, or trip name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 bg-ink-surface border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
            data-testid="search-input"
          />
        </div>

        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button
              className="h-12 px-6 bg-[#D4AF37] hover:bg-[#F3C853] text-black font-medium text-xs uppercase tracking-[0.25em] transition-all duration-200 hover:-translate-y-[1px]"
              data-testid="new-itinerary-btn"
            >
              <Plus size={18} weight="bold" /> New Itinerary
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-ink-surface border-white/10 text-white max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl text-white">
                New <span className="italic text-[#D4AF37]">Journey</span>
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2" data-testid="create-itinerary-form">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Trip name *</Label>
                <Input
                  required
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. Amalfi Coast Escape"
                  className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                  data-testid="new-title-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Client name *</Label>
                  <Input
                    required
                    value={draft.client_name}
                    onChange={(e) => setDraft({ ...draft, client_name: e.target.value })}
                    placeholder="e.g. Mr. & Mrs. Johnson"
                    className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                    data-testid="new-client-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Client email *</Label>
                  <Input
                    required
                    type="email"
                    value={draft.client_email}
                    onChange={(e) => setDraft({ ...draft, client_email: e.target.value })}
                    placeholder="client@email.com"
                    className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                    data-testid="new-client-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Client phone</Label>
                  <Input
                    type="tel"
                    value={draft.client_phone}
                    onChange={(e) => setDraft({ ...draft, client_phone: e.target.value })}
                    placeholder="+1 555 555 0100"
                    className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                    data-testid="new-client-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Destination *</Label>
                  <Input
                    required
                    value={draft.destination}
                    onChange={(e) => setDraft({ ...draft, destination: e.target.value })}
                    placeholder="e.g. Positano, Italy"
                    className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                    data-testid="new-destination-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Start date *</Label>
                  <Input
                    required
                    type="date"
                    value={draft.start_date}
                    onChange={(e) => setDraft({ ...draft, start_date: e.target.value })}
                    className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                    data-testid="new-start-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">End date *</Label>
                  <Input
                    required
                    type="date"
                    value={draft.end_date}
                    onChange={(e) => setDraft({ ...draft, end_date: e.target.value })}
                    className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                    data-testid="new-end-date-input"
                  />
                </div>
              </div>
              <DialogFooter className="pt-2">
                <Button
                  type="submit"
                  disabled={creating}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black w-full h-11 text-xs uppercase tracking-[0.25em]"
                  data-testid="create-submit-btn"
                >
                  {creating ? <CircleNotch size={18} className="animate-spin" /> : "Create & Edit"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates — read-only luxury samples agents can duplicate to start fast */}
      {itineraries !== null && templates.length > 0 && (
        <section className="mb-12" data-testid="templates-section">
          <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-sans mb-1.5">Sample Templates</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-white leading-tight">
                Curated <span className="italic text-[#D4AF37]">starting points</span>
              </h2>
              <p className="text-white/50 text-sm font-sans mt-2 max-w-xl">
                Read-only samples from our travel directors. Click <span className="text-[#D4AF37]">"Use this template"</span> to duplicate one as your own editable trip.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="templates-grid">
            {templates.map((t) => (
              <article
                key={t.id}
                className="group border border-[#D4AF37]/30 bg-gradient-to-br from-ink-surface to-black rounded-lg overflow-hidden transition-all duration-300 hover:border-[#D4AF37]/70 hover:-translate-y-1 relative"
                data-testid={`template-card-${t.id}`}
              >
                {/* DEMO ribbon */}
                <div className="absolute top-3 left-3 z-10 px-2.5 py-1 bg-[#D4AF37] text-black text-[9px] uppercase tracking-[0.25em] font-sans font-bold rounded">
                  Sample
                </div>

                <Link to={`/itineraries/${t.id}`} className="block">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {t.cover_image_url ? (
                      <img
                        src={t.cover_image_url}
                        alt={t.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-zinc-900 to-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-serif text-2xl text-white mb-1 line-clamp-1">{t.title}</h3>
                      {t.destination && (
                        <p className="flex items-center gap-1.5 text-xs text-[#D4AF37] uppercase tracking-widest font-sans">
                          <MapPin size={14} weight="duotone" /> {t.destination}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>

                <div className="p-4 flex items-center justify-between gap-2 border-t border-white/5">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-white/40 font-sans">Includes</p>
                    <p className="text-sm text-white/70 truncate font-sans">
                      {(t.flights?.length || 0)} flight{t.flights?.length === 1 ? "" : "s"} ·{" "}
                      {(t.hotels?.length || 0)} hotel{t.hotels?.length === 1 ? "" : "s"} ·{" "}
                      {(t.days?.length || 0)} day{t.days?.length === 1 ? "" : "s"}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleDuplicate(t.id)}
                    className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-[10px] uppercase tracking-widest h-9 px-3 shrink-0"
                    data-testid={`use-template-btn-${t.id}`}
                  >
                    <Copy size={12} weight="duotone" /> Use template
                  </Button>
                </div>

                {t.start_date && (
                  <div className="px-4 pb-3 flex items-center gap-3 text-[11px] text-white/40 font-sans">
                    <span className="flex items-center gap-1.5">
                      <CalendarBlank size={12} weight="duotone" /> {formatDate(t.start_date)} → {formatDate(t.end_date)}
                    </span>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* List */}
      {itineraries === null ? (
        <div className="py-20 flex justify-center">
          <CircleNotch size={28} className="text-[#D4AF37] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-lg p-16 text-center" data-testid="empty-state">
          <Suitcase size={40} className="text-[#D4AF37] mx-auto mb-4" weight="duotone" />
          <h3 className="font-serif text-2xl text-white mb-2">No itineraries yet</h3>
          <p className="text-white/50 text-sm mb-6 font-sans">Create your first bespoke journey.</p>
          <Button
            onClick={() => setOpenNew(true)}
            className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] h-11 px-6"
            data-testid="empty-create-btn"
          >
            <Plus size={18} weight="bold" /> Create Itinerary
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="itineraries-grid">
          {filtered.map((it) => (
            <article
              key={it.id}
              className="group border border-[#D4AF37]/20 bg-ink-surface rounded-lg overflow-hidden transition-all duration-300 hover:border-[#D4AF37]/60 hover:-translate-y-1"
              data-testid={`itinerary-card-${it.id}`}
            >
              <Link to={`/itineraries/${it.id}`} className="block">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={it.cover_image_url}
                    alt={it.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <StatusBadge status={it.status || "Draft"} />
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-serif text-2xl text-white mb-1 line-clamp-1">{it.title}</h3>
                    {it.destination && (
                      <p className="flex items-center gap-1.5 text-xs text-[#D4AF37] uppercase tracking-widest font-sans">
                        <MapPin size={14} weight="duotone" /> {it.destination}
                      </p>
                    )}
                  </div>
                </div>
              </Link>

              <div className="p-5 flex items-center justify-between border-t border-white/5">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-white/40 font-sans">Client</p>
                  <p className="text-sm text-white/80 truncate font-sans">{it.client_name || "—"}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {PAID_STATUSES.includes(it.status) && (
                    <Button
                      size="sm"
                      onClick={() => handleMarkBooked(it.id)}
                      className="bg-green-600 hover:bg-green-500 text-white text-[10px] uppercase tracking-widest h-8"
                      data-testid={`mark-booked-btn-${it.id}`}
                    >
                      <CheckCircle size={14} weight="duotone" /> Mark booked
                    </Button>
                  )}
                  <Link to={`/itineraries/${it.id}`}>
                    <Button size="sm" variant="ghost" className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid={`edit-btn-${it.id}`}>
                      <PencilSimple size={16} weight="duotone" />
                    </Button>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" className="text-white/70 hover:text-red-400 hover:bg-red-500/10" data-testid={`delete-btn-${it.id}`}>
                        <Trash size={16} weight="duotone" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif text-2xl">Delete itinerary?</AlertDialogTitle>
                        <AlertDialogDescription className="text-white/60 font-sans">
                          This will permanently remove "{it.title}". This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(it.id)}
                          className="bg-red-500 hover:bg-red-600 text-white"
                          data-testid={`confirm-delete-${it.id}`}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              <div className="px-5 pb-4 flex items-center gap-4 text-[11px] text-white/50 font-sans">
                <span className="flex items-center gap-1.5">
                  <CalendarBlank size={13} weight="duotone" /> {formatDate(it.start_date)} → {formatDate(it.end_date)}
                </span>
              </div>
            </article>
          ))}
        </div>
      )}
    </AgentLayout>
  );
}
